import { Button, HorizontalDimmer, Spinner, TextField } from '@mgh-app/component-library';
import { MainLayoutContext } from 'layouts/MainLayout';
import debounce from 'lodash.debounce';
import { LightingPreset } from 'models/Thing';
import { useAppNavigation } from 'navigation/useAppNavigation';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { LIGHTS_DIMMER_DELAY } from 'services/apiConfig';
import { selectRoomById } from 'services/RoomService';
import {
  selectPreset,
  selectThing,
  useCreateNewPresetMutation,
  useGetChildrenQuery,
  useGetLightingPresetsQuery,
  useSetLightValueMutation,
  useUpdatePresetMutation,
} from 'services/ThingsService';
import { useAppSelector } from 'store';
import {
  display,
  fontSize,
  gridAutoRows,
  gridTemplateColumns,
  gridTemplateRows,
  justifyContent,
  justifyItems,
  margin,
  overflow,
  twCls,
  width,
} from 'style';

export const PresetDetails = (): JSX.Element => {
  const { setTitle, showNavigationButtons } = useContext(MainLayoutContext);
  const { groupId = '', roomId = '', presetId = 'new' } = useParams();

  const room = useAppSelector((state) => selectRoomById(state, roomId));
  const thing = useAppSelector((state) => selectThing(state, roomId, groupId));
  const preset = useAppSelector((state) => selectPreset(state, groupId, presetId));
  const [currentPreset, setCurrentPreset] = useState<LightingPreset>(
    preset || ({ name: '', lights: {} } as LightingPreset),
  );
  const [validationError, setValidationError] = useState<Record<string, string>>({});

  const [createNewPreset] = useCreateNewPresetMutation();
  const [updatePreset] = useUpdatePresetMutation();
  const { data: children, isLoading: isLoadingChildren } = useGetChildrenQuery(groupId, {
    refetchOnMountOrArgChange: true,
  });
  const { refetch: refetchPresets } = useGetLightingPresetsQuery(groupId);
  const [setLightValue] = useSetLightValueMutation();
  const { navigateLightingGroup } = useAppNavigation();

  const isLoading = isLoadingChildren;

  useEffect(() => {
    room?.nickName && setTitle(room?.nickName);
    showNavigationButtons(true);
  }, [room, room?.nickName, setTitle, showNavigationButtons]);

  const savePreset = async () => {
    if (!currentPreset.name?.trim()?.length) {
      setValidationError({ ...validationError, name: 'Please enter preset name' });
    } else {
      presetId === 'new'
        ? createNewPreset({
            groupId,
            name: currentPreset.name?.trim(),
            presets: currentPreset.lights,
          })
        : updatePreset({
            groupId,
            presetId,
            name: currentPreset.name?.trim(),
            presets: currentPreset.lights,
          });
      await refetchPresets();
      navigateLightingGroup(roomId, groupId);
    }
  };

  const changeLightsBrightness = useMemo(
    () =>
      debounce(
        (groupId: string, value: number) =>
          setLightValue({
            lightIds:
              children?.groups
                .find((group) => group.id === groupId)
                ?.children.map((light) => light.id) || [],
            brightness: Math.round(value),
            power: 'ON',
          }),
        LIGHTS_DIMMER_DELAY,
        { leading: false, trailing: true },
      ),
    [children?.groups, setLightValue],
  );

  const onDimmerChange = (groupId: string, value: number) => {
    changeLightsBrightness(groupId, value);
    setCurrentPreset({
      ...currentPreset,
      lights: {
        ...currentPreset.lights,
        [groupId]: value,
      },
    });
  };

  return (
    <div
      className={twCls(
        margin('mt-6'),
        display('grid'),
        gridTemplateColumns('grid-cols-1'),
        gridTemplateRows('grid-rows-min-fc-min'),
        justifyContent('justify-center'),
        justifyItems('justify-items-center'),
      )}
    >
      <h2 className={twCls(fontSize('text-sm'))}>
        {thing?.name} {'>'} {presetId === 'new' ? 'New Preset' : 'Edit Preset'}
      </h2>
      <div
        className={twCls(
          width('w-full'),
          margin('mt-6'),
          overflow('overflow-auto'),
          display('grid'),
          gridAutoRows('auto-rows-min'),
          gridTemplateColumns('grid-cols-1'),
        )}
      >
        {isLoading && <Spinner />}
        {!isLoading && (
          <>
            <TextField
              required
              error={validationError['name']?.length > 0}
              helperText={validationError['name']}
              className={twCls(margin('mb-7'))}
              placeholder="Name of Preset"
              value={currentPreset.name}
              onChange={(ev: { target: { value: string } }) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { name: _, ...resetValidationError } = validationError;
                setValidationError(resetValidationError);
                setCurrentPreset((val) => ({ ...val, name: ev.target.value }));
              }}
            />
            {children &&
              children.groups.map((group) => (
                <div key={group.name} className={margin('mt-3')}>
                  <h2 key={`${group.name}-name`} className={margin('mb-1')}>
                    {group.name}
                  </h2>
                  <HorizontalDimmer
                    key={`${group.name}-slider`}
                    value={currentPreset.lights[group.id] || 0}
                    onChange={(_: Event, val: number | number[]) =>
                      onDimmerChange(group.id, Array.isArray(val) ? val[0] : val)
                    }
                  />
                </div>
              ))}
          </>
        )}
      </div>
      <div
        className={twCls(
          width('w-full'),
          display('grid'),
          gridTemplateColumns('grid-cols-1'),
          gridTemplateRows('grid-rows-min-content-5'),
        )}
      >
        <Button onClick={savePreset}>Save Preset</Button>
      </div>
    </div>
  );
};
